import {
  executeOptimisticMutation,
  optimisticMutationDispositionOf,
  prependUnique,
  remove,
  select,
  update,
} from '@graphql-cache/exchange/optimistic';
import { type Client, createRequest, type OperationResult } from '@urql/core';
import { v4 as uuidv4 } from 'uuid';
import type { Favorite } from './generated/schemas/favorite';
import type { FavoriteEntityType } from './generated/schemas/favoriteEntityType';
import type { ReorderFavoritesRequest } from './generated/schemas/reorderFavoritesRequest';
import {
  type FavoriteFieldsFragment,
  FavoritesDocument,
  type FavoritesQueryVariables,
  type GraphqlEntityType,
  ReorderFavoritesDocument,
  type ReorderFavoritesMutation,
  type ReorderFavoritesMutationVariables,
  SetFavoriteDocument,
  type SetFavoriteMutation,
  type SetFavoriteMutationVariables,
} from './graphql/generated/graphql';

const FAVORITE_ENTITY_TYPE_TO_GRAPHQL = {
  user: 'USER',
  chat: 'CHAT',
  channel: 'CHANNEL',
  channel_message: 'CHANNEL_MESSAGE',
  document: 'DOCUMENT',
  project: 'PROJECT',
  email_thread: 'EMAIL_THREAD',
  calendar_event: 'CALENDAR_EVENT',
  team: 'TEAM',
  call: 'CALL',
  foreign_entity: 'FOREIGN_ENTITY',
  static_file: 'STATIC_FILE',
  crm_company: 'CRM_COMPANY',
  crm_contact: 'CRM_CONTACT',
  crm_pipeline: 'CRM_PIPELINE',
  reminder: 'REMINDER',
  skill: 'SKILL',
  agent_session: 'AGENT_SESSION',
  scheduled_action: 'SCHEDULED_ACTION',
  initiative: 'INITIATIVE',
  database: 'DATABASE',
  database_row: 'DATABASE_ROW',
  form: 'FORM',
  external_chat: 'EXTERNAL_CHAT',
} satisfies Record<FavoriteEntityType, GraphqlEntityType>;

const GRAPHQL_ENTITY_TYPE_TO_FAVORITE = {
  AGENT_SESSION: 'agent_session',
  CALENDAR_EVENT: 'calendar_event',
  CALL: 'call',
  CHANNEL: 'channel',
  CHANNEL_MESSAGE: 'channel_message',
  CHAT: 'chat',
  CRM_COMPANY: 'crm_company',
  CRM_CONTACT: 'crm_contact',
  CRM_PIPELINE: 'crm_pipeline',
  DATABASE: 'database',
  DATABASE_ROW: 'database_row',
  DOCUMENT: 'document',
  EMAIL_THREAD: 'email_thread',
  FOREIGN_ENTITY: 'foreign_entity',
  FORM: 'form',
  INITIATIVE: 'initiative',
  PROJECT: 'project',
  REMINDER: 'reminder',
  SKILL: 'skill',
  SCHEDULED_ACTION: 'scheduled_action',
  STATIC_FILE: 'static_file',
  TEAM: 'team',
  USER: 'user',
  EXTERNAL_CHAT: 'external_chat',
} satisfies Record<ReturnedGraphqlEntityType, FavoriteEntityType>;

/** `WHATSAPP_GROUP` is a schema-only stub kept for released clients; the
 * server never returns it. Remove with it after 2026-11-30. */
type ReturnedGraphqlEntityType = Exclude<GraphqlEntityType, 'WHATSAPP_GROUP'>;

/** Convert a REST favorite entity type into its GraphQL equivalent. */
export function toGraphqlFavoriteEntityType(
  entityType: FavoriteEntityType
): GraphqlEntityType {
  return FAVORITE_ENTITY_TYPE_TO_GRAPHQL[entityType];
}

/** Convert one GraphQL favorite into the shared favorites-list shape. */
export function mapGraphqlFavorite(
  favorite: FavoriteFieldsFragment
): (Favorite & { id: string }) | undefined {
  const { entityType } = favorite;
  if (entityType === 'WHATSAPP_GROUP') return undefined;
  return {
    // urql-solid reconciles lists by id. Preserve it so a mounted row's
    // preview/avatar subscriptions keep pointing at the same entity.
    id: favorite.id,
    channelId: favorite.channelId,
    channelType: favorite.channelType,
    createdAt: favorite.createdAt,
    documentSubType: favorite.documentSubType,
    entityId: favorite.entityId,
    entityType: GRAPHQL_ENTITY_TYPE_TO_FAVORITE[entityType],
    fileType: favorite.fileType,
    sortOrder: favorite.sortOrder,
  };
}

/** Input for setting one entity's favorite state. */
export type SetFavoriteArgs = {
  entityType: FavoriteEntityType;
  entityId: string;
};

/** Explicit null keeps unfiltered reads, link patches, and revalidations on the
 * same cache field; the cache cannot resolve an omitted optional variable. */
export const UNFILTERED_FAVORITES_VARIABLES = {
  filter: null,
} satisfies FavoritesQueryVariables;

export type FavoritesCacheTarget = {
  variables: FavoritesQueryVariables;
  updateCachedList: boolean;
};

type FavoriteEffects = Extract<
  SetFavoriteMutation['setFavorite']['result'],
  { __typename: 'GraphqlMutationSuccess' }
>['effects'];

function favoriteSoupEffects(
  args: SetFavoriteArgs,
  favorite: boolean
): FavoriteEffects {
  const typenames = {
    document: 'GraphqlSoupDocument',
    project: 'GraphqlSoupProject',
    reminder: undefined,
    chat: 'GraphqlSoupChat',
    channel: 'GraphqlSoupChannel',
    channel_message: 'GraphqlSoupChannelMessage',
    email_thread: 'GraphqlSoupEmailThread',
    calendar_event: 'GraphqlSoupCalendarEvent',
    call: 'GraphqlSoupCall',
    crm_company: 'GraphqlSoupCrmCompany',
    foreign_entity: 'GraphqlSoupForeignEntity',
    agent_session: 'GraphqlSoupAgentSession',
    user: undefined,
    team: undefined,
    static_file: undefined,
    crm_contact: undefined,
    crm_pipeline: undefined,
    skill: undefined,
    scheduled_action: undefined,
    initiative: 'GraphqlSoupInitiative',
    database: undefined,
    database_row: undefined,
    // Forms are not favoritable; the service refuses them.
    form: undefined,
    // Standalone groups have no Soup entity.
    external_chat: undefined,
  } as const satisfies Record<FavoriteEntityType, string | undefined>;
  const typename = typenames[args.entityType];
  return typename
    ? [
        {
          __typename: 'SoupUpdated',
          item: {
            __typename: typename,
            id: args.entityId,
            isFavorited: favorite,
          },
        },
      ]
    : [];
}

/** Submit a durable optimistic GraphQL add/remove favorite mutation. */
export function executeGraphqlSetFavoriteMutation(
  client: Client,
  args: SetFavoriteArgs,
  favorite: boolean,
  optimisticSortOrder: number,
  cacheTargets: readonly FavoritesCacheTarget[] = [
    { variables: UNFILTERED_FAVORITES_VARIABLES, updateCachedList: true },
  ]
): Promise<OperationResult<SetFavoriteMutation, SetFavoriteMutationVariables>> {
  const entityType = toGraphqlFavoriteEntityType(args.entityType);
  const optimisticFavorite: FavoriteFieldsFragment = {
    __typename: 'GraphqlFavorite',
    id: `${args.entityType}:${args.entityId}`,
    entityType,
    entityId: args.entityId,
    sortOrder: optimisticSortOrder,
    createdAt: new Date().toISOString(),
    fileType: null,
    documentSubType: null,
    channelType: null,
    channelId: null,
  };
  const identity = {
    __typename: optimisticFavorite.__typename,
    id: optimisticFavorite.id,
  };
  const optimisticData: SetFavoriteMutation = {
    setFavorite: {
      __typename: 'SetFavoritePayload',
      result: {
        __typename: 'GraphqlMutationSuccess',
        effects: favoriteSoupEffects(args, favorite),
      },
      favorite: favorite ? optimisticFavorite : null,
    },
  };

  return executeOptimisticMutation(
    client,
    SetFavoriteDocument,
    {
      entity: { type: entityType, id: args.entityId },
      favorite,
    },
    optimisticData,
    {
      // Membership changes also affect ordering: removing then re-adding
      // appends a favorite. Coalescing that pair into an add would preserve
      // the server's old position instead. Keep each toggle in queue order.
      uuid: uuidv4(),
      // A cold offline cache has no user.favorites field to patch. The
      // optimistic mutation itself can still be durably queued; replay
      // revalidation populates the list once the network is available.
      updates: cacheTargets
        .filter((target) => target.updateCachedList)
        .map((target) =>
          update(
            select(FavoritesDocument, target.variables)
              .field('user')
              .field('favorites'),
            favorite ? prependUnique(identity) : remove(identity)
          )
        ),
      revalidations: cacheTargets.map((target) => ({
        document: FavoritesDocument,
        variables: target.variables,
      })),
    }
  ).toPromise();
}

/** Whether a favorites write committed remotely or was accepted by the offline queue. */
export type FavoriteWriteResult =
  | { kind: 'committed' }
  | { kind: 'queued'; transactionId: string };

export function graphqlSetFavoriteResult(
  result: OperationResult<SetFavoriteMutation, SetFavoriteMutationVariables>
): FavoriteWriteResult {
  const disposition = optimisticMutationDispositionOf(result);
  // Superseded operations intentionally carry no data. They are accepted via
  // their replacement, not missing-data errors. Also supports older queued work.
  if (disposition?.kind === 'queued') {
    return { kind: 'queued', transactionId: disposition.transactionId };
  }
  if (disposition?.kind === 'permanently-failed') throw disposition.error;
  if (result.error) throw result.error;
  const payload = result.data?.setFavorite;
  if (!payload) throw new Error('setFavorite mutation returned no data');
  if (payload.result.__typename === 'GraphqlMutationError') {
    throw new Error(payload.result.message);
  }
  return { kind: 'committed' };
}

/**
 * Reorders describe the complete value of one user-owned slot, so a newer
 * offline reorder can safely replace an older queued reorder.
 */
const REORDER_FAVORITES_OPTIMISTIC_UUID =
  '86cc4bfe-c45a-4e28-880a-6ba5ca921d35';

/** Submit a durable optimistic GraphQL favorites reorder. */
export function executeGraphqlReorderFavoritesMutation(
  client: Client,
  args: ReorderFavoritesRequest,
  revalidationVariables: readonly FavoritesQueryVariables[] = [
    UNFILTERED_FAVORITES_VARIABLES,
  ]
): Promise<
  OperationResult<ReorderFavoritesMutation, ReorderFavoritesMutationVariables>
> {
  const favorites = args.favorites.map((favorite, sortOrder) => ({
    __typename: 'GraphqlFavorite' as const,
    id: `${favorite.entityType}:${favorite.entityId}`,
    entityType: toGraphqlFavoriteEntityType(favorite.entityType),
    entityId: favorite.entityId,
    sortOrder,
  }));
  const variables: ReorderFavoritesMutationVariables = {
    input: {
      favorites: favorites.map((favorite) => ({
        type: favorite.entityType,
        id: favorite.entityId,
      })),
    },
  };
  const optimisticData: ReorderFavoritesMutation = {
    reorderFavorites: favorites,
  };
  // An empty reorder is a no-op, not a replacement for an existing queued order.
  if (favorites.length === 0) {
    return Promise.resolve({
      operation: client.createRequestOperation(
        'mutation',
        createRequest(ReorderFavoritesDocument, variables)
      ),
      data: optimisticData,
      stale: false,
      hasNext: false,
    });
  }
  return executeOptimisticMutation(
    client,
    ReorderFavoritesDocument,
    variables,
    optimisticData,
    {
      uuid: REORDER_FAVORITES_OPTIMISTIC_UUID,
      revalidations: revalidationVariables.map((variables) => ({
        document: FavoritesDocument,
        variables,
      })),
    }
  ).toPromise();
}

/** Interpret a GraphQL reorder operation as a caller-facing disposition. */
export function graphqlReorderFavoritesResult(
  result: OperationResult<
    ReorderFavoritesMutation,
    ReorderFavoritesMutationVariables
  >
): FavoriteWriteResult {
  const disposition = optimisticMutationDispositionOf(result);
  if (disposition?.kind === 'queued') {
    return {
      kind: 'queued',
      transactionId: disposition.transactionId,
    };
  }
  if (disposition?.kind === 'permanently-failed') {
    throw disposition.error;
  }
  if (result.error) throw result.error;
  if (!result.data) {
    throw new Error('reorderFavorites mutation returned no data');
  }

  return { kind: 'committed' };
}
